import { IDropdownOption } from 'office-ui-fabric-react';
import { WsDecodeBoardInterface, WsDecodeMetric, WsDecodeSource } from '../../../../../core/src/interfaces';
import { getLocalAuthorityOptions, getRegionalPartnershipOptions, getSchoolOptions, getSchoolTypeOptions } from '../../../decode-filters';
import { WsDecodeMeasurement, WsDecodeScoreView } from '../../../enums';
import { isNumeric } from '../../../helpers';
import { WsDecodeContextProps } from '../DecodeContext';
import { getMetricStatistic, getMetricStatisticValue, getMetricValueString, getMetricValueStringPercent, getNumericValueBasedOnFilter } from './decode-data';

export interface DecodeScoreCardMetric {
  id: string;
  name: string;
  displayMode: WsDecodeScoreView;
  displayedValue: string;
  currentValue: number;
  baselineValue: number;
  configuredBaselineValue: number;
  barValuePercent: number;
  measurement: WsDecodeMeasurement;
  weight: number;
  source: {
    id: string;
    name: string | null;
    kind: 'data-source' | 'board';
  };
}

interface DecodeAgentFilterSelection {
  id: string;
  name: string | null;
}

const getDisplayedMetricValue = (currentValue: number, baselineValue: number, displayMode: WsDecodeScoreView): string => {
  if (!(currentValue >= 0)) {
    return '';
  }

  if (displayMode === WsDecodeScoreView.Value) {
    return currentValue.toFixed(0);
  }

  if (displayMode === WsDecodeScoreView.Percent) {
    return getMetricValueStringPercent(currentValue, baselineValue);
  }

  return getMetricValueString(currentValue, baselineValue);
};

/** @agentsNote These rows drive both the visible card and DEX Bot context; filter before indexing so filter adjustments match the card's displayed metric order. */
export const getDecodeScoreCardMetrics = (metrics: WsDecodeMetric[], sourceList: WsDecodeSource[], boardList: WsDecodeBoardInterface[], filterId: string): DecodeScoreCardMetric[] => {
  return metrics.filter((metric) => metric.visible).map<DecodeScoreCardMetric>((metric, index) => {
    const sourceItem = sourceList.find((source) => source.id === metric.sourceId);
    const metricStatistic = getMetricStatistic(sourceList, boardList, metric, sourceItem ? sourceItem.dataValueList : []);
    const currentValue = getNumericValueBasedOnFilter(getMetricStatisticValue(metric, metricStatistic), filterId, index);
    const barValuePercent = getNumericValueBasedOnFilter(metricStatistic.metricValue, filterId, index);
    const isBoardSource = isNumeric(metric.sourceId);
    const sourceBoard = isBoardSource ? boardList.find((board) => board.id === Number(metric.sourceId)) : undefined;

    return {
      id: metric.id,
      name: metric.name,
      displayMode: metric.scoreView,
      displayedValue: getDisplayedMetricValue(currentValue, metricStatistic.baselineValue, metric.scoreView),
      currentValue,
      baselineValue: metricStatistic.baselineValue,
      configuredBaselineValue: metric.baselineValue,
      barValuePercent,
      measurement: metric.measurement,
      weight: metric.weight,
      source: {
        id: metric.sourceId,
        name: isBoardSource ? (sourceBoard ? sourceBoard.name : null) : (sourceItem ? sourceItem.name : null),
        kind: isBoardSource ? 'board' : 'data-source'
      }
    };
  });
};

const getFilterSelection = (selectedId: string | undefined, getOptions: () => IDropdownOption[]): DecodeAgentFilterSelection | undefined => {
  if (!selectedId) {
    return undefined;
  }

  const selectedOption = getOptions().find((option) => `${option.key}` === selectedId);

  return {
    id: selectedId,
    name: selectedOption ? selectedOption.text : null
  };
};

export const createDecodeAgentContext = (boardItem: WsDecodeBoardInterface, metrics: DecodeScoreCardMetric[], decodeContext: WsDecodeContextProps | undefined): string => {
  return JSON.stringify({
    board: {
      id: boardItem.id,
      name: boardItem.name,
      description: typeof boardItem.configuration.description === 'string' ? boardItem.configuration.description : ''
    },
    filters: {
      dateRange: {
        start: decodeContext?.selectedDateRange.start?.toISOString() || null,
        end: decodeContext?.selectedDateRange.end?.toISOString() || null
      },
      regionalPartnership: getFilterSelection(decodeContext?.selectedRegionalPartnershipId, getRegionalPartnershipOptions),
      localAuthority: getFilterSelection(decodeContext?.selectedLocalAuthorityId, () => getLocalAuthorityOptions(decodeContext?.selectedRegionalPartnershipId)),
      schoolType: getFilterSelection(decodeContext?.selectedSchoolTypeId, () => getSchoolTypeOptions(decodeContext?.selectedRegionalPartnershipId)),
      school: getFilterSelection(decodeContext?.selectedSchoolId, () => getSchoolOptions(decodeContext?.selectedRegionalPartnershipId, decodeContext?.selectedLocalAuthorityId))
    },
    metrics
  });
};
